import React, {useState, useRef, useEffect} from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  Input,
  Button,
  Alert,
  FormGroup,
  Label,
} from 'reactstrap';
import {FaEdit} from "react-icons/fa";
import {askPolicyAIStream} from "../../services/intelligence/policyChatbotServices";

const POLICIES = {
  "Ethiopia Cooperative Organisation Directive": "Ethiopia_cooperative_organisation_directive.txt",
  "Ethiopia Seed Proclamation": "Ethiopia_seed_proclamation.txt",
  "Ethiopia Water Policy": "Ethiopia_water_policy.txt",
  "Ghana National Seed Policy": "Ghana_national_seed_policy.txt",
  "Ghana National Youth Policy": "Ghana_national_youth_policy.txt",
  "Nigeria Agricultural Policy": "Nigeria_agricultural_policy.txt",
  "Nigeria National Food and Nutrition Policy": "Nigeria_national_food_and_nutrition_policy.txt",
  "Nigeria National Seed Policy": "Nigeria_national_seed_policy.txt"
};

const LOADING_MESSAGES = [
  "Analyzing the policy document...",
  "Processing your question...",
  "Searching through relevant sections...",
  "Preparing a comprehensive response...",
  "Almost there..."
];

const PolicyChatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const [question, setQuestion] = useState('');
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMessageIndex, setLoadingMessageIndex] = useState(0);
  const [copyStatus, setCopyStatus] = useState({});
  const [abortController, setAbortController] = useState(null); // Store abort controller for canceling stream
  const messagesEndRef = useRef(null);

  const toggleModal = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      setSelectedPolicy(null);
      setQuestion('');
      setMessages([]);
      setLoadingMessageIndex(0);
      setAbortController(null);  // Clear abort controller when modal closes
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({behavior: 'smooth'});
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSend = async () => {
    if (!selectedPolicy || !question.trim()) return;

    setMessages(prev => [...prev, {type: 'user', content: question}]);
    setQuestion('');
    setLoading(true);

     // Reset the loading message index when sending a new question
     setLoadingMessageIndex(0);

     setMessages(prev => [...prev, {type: 'bot', content: ''}]);

    // Start rotating loading messages immediately
    let index = 0;
    const interval = setInterval(() => {
      setLoadingMessageIndex(prev => {
        const nextIndex = (prev + 1) % LOADING_MESSAGES.length;
        return nextIndex;
      });
    }, 5000);

    // Start streaming immediately after the last loading message
    const startStreaming = async () => {
      await new Promise(resolve => setTimeout(resolve, (LOADING_MESSAGES.length - 1) * 5000));
      clearInterval(interval); // Stop the loading messages once streaming starts

      try {
        const controller = new AbortController(); // Create a new abort controller for this request
        setAbortController(controller); // Save it to state so we can reference it later

        const stream = await askPolicyAIStream(POLICIES[selectedPolicy], question, {signal: controller.signal});
        const reader = stream.getReader();
        const decoder = new TextDecoder();
        let finalAnswer = '';
        let done = false;

        while (!done) {
          if (controller.signal.aborted) break; // Check if the signal has been aborted

          const {value, done: streamDone} = await reader.read();
          if (value) {
            const chunk = decoder.decode(value);
            finalAnswer += chunk;
            setMessages(prev => {
              const updated = [...prev];
              updated[updated.length - 1] = {
                type: 'bot',
                content: finalAnswer
              };
              return updated;
            });
          }
          done = streamDone;
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          setMessages(prev => [
            ...prev,
            {
              type: 'error',
              content: 'Sorry, I encountered an error while processing your question. Please try again.'
            }
          ]);
        }
      } finally {
        setLoading(false);  // Reset loading state even after error or successful completion
      }
    };

    startStreaming();  // Trigger the streaming to start
  };

  const handleCancel = () => {
    if (abortController) {
      abortController.abort(); // Abort the current streaming request
      setAbortController(null); // Clear the abort controller so it’s ready for the next request
    }

    setLoading(false);  // Stop the loading spinner
    setMessages(prev => [...prev]);  // Retain previous messages
    setQuestion('');    // Clear the question input
    setLoadingMessageIndex(0);  // Reset the loading message index
  };

  const handleEdit = (content) => {
    setQuestion(content); // Set the content back into the input field for editing
  };

  const handleCopy = (content, index) => {
    navigator.clipboard.writeText(content);
    setCopyStatus(prev => ({...prev, [index]: true}));
    setTimeout(() => {
      setCopyStatus(prev => ({...prev, [index]: false}));
    }, 2000);
  };

  const renderMessage = (message, index) => {
    const isLastMessage = index === messages.length - 1;

    if (message.type === 'user') {
      return (
        <div key={index} className="flex justify-end mb-3">
          <div className="bg-primary text-white rounded-lg p-3 max-w-[80%] whitespace-pre-wrap shadow-sm"
               style={{
                 borderBottomRightRadius: '0.25rem',
                 borderTopRightRadius: '1rem',
                 borderBottomLeftRadius: '1rem',
                 borderTopLeftRadius: '1rem'
               }}>
            <div className="flex items-center justify-between">
              <button
                className="mr-2 text-sm"
                style={{color: '#fff71f'}} // Apply the custom yellow color for the text (icon)
                onClick={() => handleEdit(message.content)}
                title="Edit"
              >
                <FaEdit/> {/* Icon with the custom color */}
              </button>

              {message.content}
            </div>
          </div>
        </div>
      );
    } else if (message.type === 'bot') {
      return (
        <div key={index} className="flex justify-start mb-3 relative group">
          <div className="bg-gray-100 rounded-lg p-3 pt-1 max-w-[80%] whitespace-pre-wrap shadow-sm"
               style={{
                 borderBottomRightRadius: '1rem',
                 borderTopRightRadius: '1rem',
                 borderBottomLeftRadius: '0.25rem',
                 borderTopLeftRadius: '1rem'
               }}>
            {message.content}
            {isLastMessage && loading && !message.content && (
              <div className="flex items-center mt-2 ">
                <div className="flex space-x-1 mr-2">
                  <div className="w-2 h-2 rounded-full bg-gray-400 animate-pulse"/>
                  <div className="w-2 h-2 rounded-full bg-gray-400 animate-pulse delay-150"/>
                  <div className="w-2 h-2 rounded-full bg-gray-400 animate-pulse delay-300"/>
                </div>
                <span className="text-sm text-gray-500">{LOADING_MESSAGES[loadingMessageIndex]}</span>
              </div>
            )}
            {!loading && message.content && (
              <div className="mt-3">
                <button
                  className={`text-sm font-medium rounded-full px-4 py-1 transition-all focus:outline-none ${
                    copyStatus[index]
                      ? 'bg-green-100 text-green-600'  // Success state
                      : 'bg-blue-100 text-blue-600 hover:bg-blue-200'  // Normal state
                  }`}
                  onClick={() => handleCopy(message.content, index)}
                >
                  {copyStatus[index] ? '✓ Copied!' : 'Copy'}
                </button>
              </div>
            )}
          </div>
        </div>
      );
    } else {
      return (
        <div key={index} className="flex justify-center mb-3">
          <Alert color="danger" className="mb-0">
            {message.content}
          </Alert>
        </div>
      );
    }
  };

  return (
    <>
      <Button
        color="primary"
        onClick={toggleModal}
        className="rounded-full px-3 py-1.5 flex items-center justify-center fixed bottom-12 sm:bottom-16 md:bottom-20 lg:bottom-16 xl:bottom-12 right-6 z-50 shadow-md space-x-1.5"
      >
        <i className="mdi mdi-message-text text-lg"/>
        <span className="hidden sm:inline text-xs font-medium">Ask APRITS AI</span>
      </Button>


      <Modal isOpen={isOpen} toggle={toggleModal} size="lg">
        <ModalHeader toggle={toggleModal}>
          Ask APRITS Policy AI
        </ModalHeader>
        <ModalBody className="h-[600px] flex flex-col">
          <Alert color="info" className="mb-3">
            <p className="mb-0">
              The quality of responses depends on the clarity of your questions.
              While our AI strives for accuracy, please refer to the actual
              policy documents for definitive information.
            </p>
          </Alert>

          <FormGroup>
            <Label for="policySelect">Select a policy to ask about</Label>
            <Input
              type="select"
              id="policySelect"
              value={selectedPolicy || ''}
              onChange={e => {
                const policy = e.target.value;
                setSelectedPolicy(policy);

                // Clear previous messages and question when a new policy is selected
                setMessages([]);
                setQuestion('');
                setLoadingMessageIndex(0);

                // Optionally, reset loading state to false if needed
                setLoading(false);
              }}
              className="mb-3"
            >
              <option value="">Select a policy...</option>
              {Object.keys(POLICIES).map(policy => (
                <option key={policy} value={policy}>
                  {policy}
                </option>
              ))}
            </Input>
          </FormGroup>

          <div className="flex-1 overflow-y-auto p-4 bg-gray-50 rounded-lg mb-4">
            {messages.length === 0 && !loading && (
              <div className="flex flex-col items-center justify-center h-full text-center text-gray-500">
                <i className="mdi mdi-message-text-outline text-4xl mb-2"/>
                <p>Select a policy and ask your question to get started</p>
              </div>
            )}
            {messages.map((message, index) => renderMessage(message, index))}
            <div ref={messagesEndRef}/>
          </div>

          <div className="flex gap-2 items-end">
            <Input
              type="textarea"
              placeholder="Type your question here..."
              value={question}
              onChange={e => setQuestion(e.target.value)}
              onKeyPress={e => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              rows="1"
              className="flex-1 resize-none transition-all duration-200 focus:border-primary focus:ring-1 focus:ring-primary"
              style={{minHeight: '44px', maxHeight: '120px'}}
            />
            <Button
              color="primary"
              onClick={handleSend}
              disabled={!selectedPolicy || !question.trim() || loading}
              className="flex items-center justify-center"
              title="Send"
            >
              <i className="mdi mdi-send" />
            </Button>
            {loading && (
              <Button
                color="danger"
                onClick={handleCancel}
                className="ml-2"
              >
                Cancel
              </Button>
            )}
          </div>

        </ModalBody>
      </Modal>
    </>
  );
};

export default PolicyChatbot;
